import { useMemo, useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { useLocation, useNavigate } from 'react-router-dom';

type BreadcrumbItem = {
  label: string;
  path: string;
  icon?: string;
  isActive: boolean;
  isCompleted: boolean;
  isClickable: boolean;
};

const Breadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [aiModeActive, setAiModeActive] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Listen for AI mode changes
  useEffect(() => {
    const handleAiModeChange = (event: CustomEvent) => {
      setAiModeActive(event.detail.isActive);
    };

    window.addEventListener('aiModeChanged', handleAiModeChange as EventListener);

    return () => {
      window.removeEventListener('aiModeChanged', handleAiModeChange as EventListener);
    };
  }, []);

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [];

    // Always start with Dashboard/Home
    items.push({
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'Home',
      isActive: location.pathname === '/dashboard',
      isCompleted: location.pathname !== '/dashboard',
      isClickable: true,
    });

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      if (currentPath === '/dashboard') return;

      // Generate dynamic labels from path segments
      let label = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      // Special cases for common patterns
      if (segment === 'create') label = 'Create';
      if (segment === 'edit') label = 'Edit';
      if (segment === 'detail') label = 'Details';
      if (segment === 'reservation-system') label = 'Reservations';
      if (segment === 'reports-analytics') label = 'Reports';
      if (segment === 'forecasting-engine') label = 'Forecasting';
      if (segment === 'historical-data-management') label = 'Historical Data';

      // Override label for report pages when AI mode is active
      if (aiModeActive && currentPath.includes('/report/')) {
        label = 'AI Assistant Report';
      }

      // UUID pattern detection
      const uuidPattern = /^[0-9a-f]{8}(-?[0-9a-f]{4}){3}-?[0-9a-f]{12}$/i;
      if (uuidPattern.test(segment)) {
        const clean = segment.replace(/-/g, '');
        label = `${clean.slice(0, 4)}...${clean.slice(-4)}`;
      }

      const isCurrentPath = currentPath === location.pathname;

      items.push({
        label,
        path: currentPath,
        isActive: isCurrentPath,
        isCompleted: !isCurrentPath && index < pathSegments.length - 1,
        isClickable: !isCurrentPath,
      });
    });

    return items;
  }, [location.pathname, aiModeActive]);

  const shouldHide = location.pathname === '/login' || breadcrumbs.length <= 1;
  if (shouldHide) return null;

  const handleNavigation = (path: string, isClickable: boolean) => {
    if (isClickable) {
      navigate(path);
    }
  };

  // Mobile view logic: show Dashboard > ... > Current when more than 3 items
  const shouldCollapse = isMobile && breadcrumbs.length > 3;

  const displayBreadcrumbs = shouldCollapse
    ? [
        breadcrumbs[0], // Dashboard
        {
          label: '...',
          path: '',
          isActive: false,
          isCompleted: false,
          isClickable: false,
        },
        breadcrumbs[breadcrumbs.length - 1], // Current page
      ]
    : breadcrumbs;

  return (
    <div className="px-2 py-1">
      <div className="flex rounded-md overflow-x-auto shadow-sm bg-gray-200 h-8 relative w-fit max-w-full border border-gray-300">
        {displayBreadcrumbs.map((item, index) => (
          <div
            key={item.path || `ellipsis-${index}`}
            className={`
              relative flex items-center text-xs font-medium transition-colors h-full whitespace-nowrap border-r border-gray-300 last:border-r-0
              ${
                item.label === '...'
                  ? 'bg-slate-500 text-white px-2 cursor-default'
                  : index === 0
                    ? 'px-3 min-w-0 cursor-pointer text-white'
                    : index === displayBreadcrumbs.length - 1
                      ? 'bg-gray-400 text-white pl-6 pr-3 min-w-0 cursor-default'
                      : 'bg-gray-200 text-gray-700 pl-6 pr-3 min-w-0 cursor-pointer'
              }
            `}
            style={{
              backgroundColor: index === 0 ? 'rgb(30 58 95 / 0.9)' : undefined,
            }}
            onClick={() => item.label !== '...' && handleNavigation(item.path, item.isClickable)}
          >
            {/* Arrow shape using pseudo-element via CSS */}
            {index < displayBreadcrumbs.length - 1 && (
              <div
                className={`
                  absolute top-0 w-8 h-8 transform scale-[0.707] rotate-45 rounded-tr-sm shadow-sm z-10
                  ${item.label === '...' ? 'bg-slate-500' : index === 0 ? '' : 'bg-gray-200'}
                `}
                style={{
                  right: '-8px',
                  boxShadow: '1px -1px rgba(0,0,0,0.15)',
                  backgroundColor: index === 0 ? 'rgb(30 58 95 / 0.9)' : undefined,
                  borderTop: '1px solid #d1d5db',
                  borderRight: '1px solid #d1d5db',
                  borderLeft: 'none',
                  borderBottom: 'none',
                }}
              />
            )}

            {/* Content */}
            <div className="flex items-center z-20 relative">
              {/* Custom icon for first item */}
              {item.icon && index === 0 && <Icon name={item.icon} size={8} className="mr-1" />}

              <span className="text-xs font-medium truncate">{item.label === '...' ? '•••' : item.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Breadcrumb;
